import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import { alpha, styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../utils/currency";
import { joinBaseContract, previewBaseContractJoin } from "../api/baseContractApi";
import ContractTermsModal from "./ContractTermsModal";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    textAlign: "center",
    backgroundColor: theme.palette.background.paper,
    height: "100%",
    transition: "0.3s",
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[2],
    position: "relative",
    overflow: "hidden",
    "&:hover": {
        boxShadow: theme.shadows[5],
        transform: "translateY(-4px)",
    },
    "@media (hover: none)": {
        "&:hover": {
            boxShadow: theme.shadows[2],
            transform: "none",
        },
    },
}));

const PREVIEW_DEBOUNCE_MS = 300;
const WITHDRAWAL_PERIOD_DAYS = 14;

const formatEuropeanDate = (isoDate) => {
    const [year, month, day] = String(isoDate || "").split("-");
    return year && month && day ? `${day}.${month}.${year}` : isoDate;
};

const BaseContractPlans = ({ offerings = [], userData, paymentCurrency = "EUR" }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [selectedOffering, setSelectedOffering] = useState(null);
    const [startDate, setStartDate] = useState("");
    const [paymentDay, setPaymentDay] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");
    const [acceptedContractText, setAcceptedContractText] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [earlyStartRequested, setEarlyStartRequested] = useState(false);
    const [errors, setErrors] = useState({});
    const [termsModalOpen, setTermsModalOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState({ open: false, severity: "success", message: "" });

    // Guards against a slow response for an earlier start date / payment day
    // overwriting the preview for the values the member has since selected.
    const previewRequestId = useRef(0);

    const orderedOfferings = [...offerings].sort((a, b) => (a.order || 0) - (b.order || 0));

    const resetForm = () => {
        setStartDate("");
        setPaymentDay("");
        setPreview(null);
        setPreviewError("");
        setPreviewLoading(false);
        setAcceptedContractText(false);
        setAcceptedTerms(false);
        setEarlyStartRequested(false);
        setErrors({});
        previewRequestId.current += 1;
    };

    const openJoin = (offering) => {
        resetForm();
        setSelectedOffering(offering);
    };

    const closeJoin = () => {
        if (submitting) return;
        setSelectedOffering(null);
        setConfirmOpen(false);
        resetForm();
    };

    const loadPreview = useCallback(async (offeringId, requestedStartDate, requestedPaymentDay) => {
        const requestId = previewRequestId.current + 1;
        previewRequestId.current = requestId;

        setPreviewLoading(true);
        setPreviewError("");

        try {
            const result = await previewBaseContractJoin(offeringId, {
                startDate: requestedStartDate,
                paymentDay: Number(requestedPaymentDay),
            });

            if (previewRequestId.current !== requestId) return;
            setPreview(result);
        } catch (error) {
            if (previewRequestId.current !== requestId) return;
            setPreview(null);
            setPreviewError(error.message || "Could not calculate the first payment.");
        } finally {
            if (previewRequestId.current === requestId) {
                setPreviewLoading(false);
            }
        }
    }, []);

    // Recalculate whenever either the start date or the payment day changes.
    useEffect(() => {
        if (!selectedOffering) return undefined;

        if (!startDate || !paymentDay) {
            previewRequestId.current += 1;
            setPreview(null);
            setPreviewError("");
            setPreviewLoading(false);
            return undefined;
        }

        const timer = setTimeout(() => {
            loadPreview(selectedOffering.id, startDate, paymentDay);
        }, PREVIEW_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [selectedOffering, startDate, paymentDay, loadPreview]);

    const paymentDayOptions = selectedOffering?.paymentDayMode === "FIXED"
        ? (selectedOffering.fixedPaymentDays || [])
        : Array.from({ length: 28 }, (_, index) => index + 1);

    const minimumValidityMonths = selectedOffering?.affiliate?.minimumContractValidityMonths ?? 2;
    const clubEmail = selectedOffering?.affiliate?.email;
    const clubPhone = selectedOffering?.affiliate?.phone;
    const withdrawalPeriodDays = preview?.withdrawalPeriodDays ?? WITHDRAWAL_PERIOD_DAYS;

    const validate = () => {
        const nextErrors = {};

        if (!startDate) {
            nextErrors.startDate = "Select a start date.";
        } else {
            const calendarDay = Number(startDate.split("-")[2]);
            if (calendarDay < 1 || calendarDay > 28) {
                nextErrors.startDate = "The start date must be between the 1st and 28th of a month.";
            }
        }
        if (!paymentDay) nextErrors.paymentDay = "Select a monthly payment due date.";
        if (!acceptedContractText) nextErrors.acceptedContractText = "You must agree to the contract text.";
        if (!acceptedTerms) nextErrors.acceptedTerms = "You must agree to the terms and conditions.";
        if (!earlyStartRequested) {
            nextErrors.earlyStartRequested = "You must confirm that the service may start before the withdrawal period ends.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const continueToConfirmation = () => {
        if (!preview || previewLoading) return;
        if (validate()) setConfirmOpen(true);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const result = await joinBaseContract(selectedOffering.id, {
                startDate,
                paymentDay: Number(paymentDay),
                acceptedContractText,
                acceptedTerms,
                earlyStartRequested,
            });

            const contract = result?.contract;
            if (!contract) throw new Error("The contract could not be created.");

            const affiliate = result.affiliate || selectedOffering.affiliate || {};

            setConfirmOpen(false);
            setSelectedOffering(null);

            navigate("/checkout", {
                state: {
                    contract,
                    plan: {
                        id: "contract-payment",
                        name: `${contract.contractType || "Monthly"} Contract Payment`,
                        price: contract.paymentAmount,
                        trainingType: contract.trainingType,
                        affiliateId: contract.affiliateId,
                        validityDays: 31,
                        sessions: 999,
                    },
                    affiliate: { id: contract.affiliateId, name: affiliate.name || "Affiliate" },
                    userData,
                    isContractPayment: true,
                },
            });
        } catch (error) {
            setConfirmOpen(false);
            setNotification({
                open: true,
                severity: "error",
                message: error.message || "Could not start the contract.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (offerings.length === 0) return null;

    return (
        <Box sx={{ width: "100%", mt: 6 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Membership contracts</Typography>
                <Typography variant="body2" color="text.secondary">
                    Join a recurring monthly contract right away. You choose the start date and the monthly payment day.
                </Typography>
            </Box>

            <Grid container spacing={3} justifyContent="center">
                {orderedOfferings.map((offering) => (
                    <Grid item xs={12} sm={6} md={4} key={offering.id}>
                        <StyledCard>
                            <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                                <Box sx={{ mb: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    <Typography component="h3" variant="h4" fontWeight="bold" textAlign="center">
                                        {offering.name}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", mb: 2 }}>
                                    <Typography component="h3" variant="h5" fontWeight="bold">
                                        {formatCurrency(offering.monthlyPrice, paymentCurrency)}
                                    </Typography>
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                                        / month
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 2, opacity: 0.8, borderColor: "divider" }} />
                                <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                                    <CheckCircleRoundedIcon color="primary" />
                                    <Typography variant="body1">
                                        {offering.durationYears} {offering.durationYears === 1 ? "year" : "years"} contract
                                    </Typography>
                                </Box>
                                <Box sx={{ mb: 2, display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                                    {(offering.trainingTypes || []).map((type) => (
                                        <Chip key={type} size="small" label={type} />
                                    ))}
                                </Box>
                                {offering.additionalInfo && (
                                    <Box sx={{ mt: "auto", mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                                            {offering.additionalInfo}
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                            <CardActions>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    onClick={() => openJoin(offering)}
                                    sx={{ borderRadius: "20px", py: 1 }}
                                >
                                    Join contract
                                </Button>
                            </CardActions>
                        </StyledCard>
                    </Grid>
                ))}
            </Grid>

            <Dialog
                open={Boolean(selectedOffering)}
                onClose={closeJoin}
                fullWidth
                maxWidth="sm"
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        m: isMobile ? 0 : 2,
                        maxHeight: isMobile ? "100%" : "calc(100% - 32px)",
                    },
                }}
            >
                <DialogTitle sx={{ pr: 1, pb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" component="span" fontWeight={700} display="block">
                                Join {selectedOffering?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {formatCurrency(selectedOffering?.monthlyPrice, paymentCurrency)} / month
                            </Typography>
                        </Box>
                        <IconButton onClick={closeJoin} edge="end" aria-label="Close">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Stack spacing={2.5}>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: "action.hover",
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                                Customer
                            </Typography>
                            <Typography fontWeight={600}>{userData?.fullName || "Name unavailable"}</Typography>
                            <Typography variant="body2" color="text.secondary">{userData?.email || "Email unavailable"}</Typography>
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: 1,
                                borderColor: "divider",
                            }}
                        >
                            <Typography variant="subtitle2" gutterBottom>Contract summary</Typography>
                            <Stack spacing={1.25} divider={<Divider flexItem />}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Monthly fee</Typography>
                                    <Typography variant="body2" fontWeight={600} textAlign="right">
                                        {formatCurrency(selectedOffering?.monthlyPrice, paymentCurrency)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Period</Typography>
                                    <Typography variant="body2" fontWeight={600} textAlign="right">
                                        {selectedOffering?.durationYears}{" "}
                                        {selectedOffering?.durationYears === 1 ? "year" : "years"}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Billing</Typography>
                                    <Typography variant="body2" fontWeight={600}>Monthly</Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Earliest termination</Typography>
                                    <Typography variant="body2" fontWeight={600} textAlign="right">
                                        {minimumValidityMonths === 0
                                            ? "Anytime"
                                            : `${minimumValidityMonths} ${minimumValidityMonths === 1 ? "month" : "months"}`}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>Training types</Typography>
                                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                        {(selectedOffering?.trainingTypes || []).length === 0 ? (
                                            <Typography variant="body2">-</Typography>
                                        ) : (
                                            (selectedOffering?.trainingTypes || []).map((type) => (
                                                <Chip key={type} size="small" label={type} />
                                            ))
                                        )}
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Contract text</Typography>
                            <Box
                                sx={{
                                    whiteSpace: "pre-line",
                                    bgcolor: "action.hover",
                                    p: 2,
                                    borderRadius: 2,
                                    maxHeight: { xs: 220, sm: 280 },
                                    overflowY: "auto",
                                    fontSize: "0.9rem",
                                    lineHeight: 1.6,
                                }}
                            >
                                {selectedOffering?.content}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 2,
                            }}
                        >
                            <TextField
                                label="Start date"
                                type="date"
                                value={startDate}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    const calendarDay = Number(value.split("-")[2]);
                                    if (value && (calendarDay < 1 || calendarDay > 28)) {
                                        setStartDate("");
                                        setErrors((current) => ({
                                            ...current,
                                            startDate: "The start date must be between the 1st and 28th of a month.",
                                        }));
                                        return;
                                    }
                                    setStartDate(value);
                                    setErrors((current) => ({ ...current, startDate: "" }));
                                }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ lang: "et-EE" }}
                                error={Boolean(errors.startDate)}
                                helperText={errors.startDate || "Day must be between 1 and 28."}
                                required
                                fullWidth
                            />

                            <FormControl fullWidth error={Boolean(errors.paymentDay)} required>
                                <InputLabel>Payment day</InputLabel>
                                <Select
                                    value={paymentDay}
                                    label="Payment day"
                                    onChange={(event) => {
                                        setPaymentDay(event.target.value);
                                        setErrors((current) => ({ ...current, paymentDay: "" }));
                                    }}
                                >
                                    {paymentDayOptions.map((day) => (
                                        <MenuItem value={day} key={day}>Day {day}</MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="caption" color={errors.paymentDay ? "error" : "text.secondary"} sx={{ mt: 0.5, ml: 1.75 }}>
                                    {errors.paymentDay || "Day of each month the fee is due."}
                                </Typography>
                            </FormControl>
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: preview ? (t) => alpha(t.palette.primary.main, 0.08) : "action.hover",
                                border: 1,
                                borderColor: preview ? "primary.light" : "transparent",
                            }}
                        >
                            <Typography variant="subtitle2" gutterBottom>First payment</Typography>

                            {previewLoading && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
                                    <CircularProgress size={18} />
                                    <Typography variant="body2" color="text.secondary">Calculating...</Typography>
                                </Box>
                            )}

                            {!previewLoading && previewError && (
                                <Alert severity="error">{previewError}</Alert>
                            )}

                            {!previewLoading && !previewError && !preview && (
                                <Typography variant="body2" color="text.secondary">
                                    Choose a start date and a payment day to see the exact first payment.
                                </Typography>
                            )}

                            {!previewLoading && !previewError && preview && (
                                <Stack spacing={0.75}>
                                    <Typography variant="h4" fontWeight="bold" color="primary">
                                        {formatCurrency(preview.firstPaymentAmount, paymentCurrency)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {preview.explanation}
                                    </Typography>
                                    {preview.isProrated && (
                                        <Typography variant="body2" color="text.secondary">
                                            Prorated days: <strong>{preview.daysUntilNextPayment}</strong>
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        Next payment: <strong>{formatEuropeanDate(preview.nextPaymentDate)}</strong>
                                        , then {formatCurrency(preview.monthlyPrice, paymentCurrency)} on day {paymentDay} of each month.
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Contract ends: <strong>{formatEuropeanDate(preview.validUntil)}</strong>
                                    </Typography>
                                </Stack>
                            )}
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: 1,
                                borderColor: "divider",
                            }}
                        >
                            <FormControlLabel
                                sx={{ alignItems: "flex-start", mr: 0 }}
                                control={
                                    <Checkbox
                                        checked={acceptedContractText}
                                        onChange={(event) => {
                                            setAcceptedContractText(event.target.checked);
                                            setErrors((current) => ({ ...current, acceptedContractText: "" }));
                                        }}
                                        sx={{ pt: 0.25 }}
                                    />
                                }
                                label="I have read and agree to the contract text above."
                            />
                            {errors.acceptedContractText && (
                                <Typography variant="caption" color="error" display="block" sx={{ ml: 4 }}>
                                    {errors.acceptedContractText}
                                </Typography>
                            )}

                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                                <FormControlLabel
                                    sx={{ alignItems: "flex-start", mr: 0, flex: 1 }}
                                    control={
                                        <Checkbox
                                            checked={acceptedTerms}
                                            onChange={(event) => {
                                                setAcceptedTerms(event.target.checked);
                                                setErrors((current) => ({ ...current, acceptedTerms: "" }));
                                            }}
                                            sx={{ pt: 0.25 }}
                                        />
                                    }
                                    label="I have read and understand the Terms and Conditions."
                                />
                                <Button variant="outlined" size="small" onClick={() => setTermsModalOpen(true)} sx={{ mt: 0.5 }}>
                                    Open Terms
                                </Button>
                            </Box>
                            {errors.acceptedTerms && (
                                <Typography variant="caption" color="error" display="block" sx={{ ml: 4 }}>
                                    {errors.acceptedTerms}
                                </Typography>
                            )}
                        </Box>

                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                                {withdrawalPeriodDays}-day right of withdrawal
                            </Typography>
                            <Typography variant="body2">
                                Because this contract is concluded at a distance, you have the right to withdraw from it
                                without giving a reason. The withdrawal period is {withdrawalPeriodDays} days and it
                                starts on the contract start date.
                                {preview?.withdrawalDeadline && (
                                    <>
                                        {" "}With your chosen start date{" "}
                                        <strong>{formatEuropeanDate(startDate)}</strong>, you can withdraw until{" "}
                                        <strong>{formatEuropeanDate(preview.withdrawalDeadline)}</strong>.
                                    </>
                                )}
                                {" "}To withdraw, send a notice to the club before the period ends. This contract remains
                                available to you at any time under "My contracts" in Irontrack, together with the date
                                you accepted it.
                            </Typography>
                        </Alert>

                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: 1,
                                borderColor: errors.earlyStartRequested ? "error.main" : "divider",
                            }}
                        >
                            <FormControlLabel
                                sx={{ alignItems: "flex-start", mr: 0 }}
                                control={
                                    <Checkbox
                                        checked={earlyStartRequested}
                                        onChange={(event) => {
                                            setEarlyStartRequested(event.target.checked);
                                            setErrors((current) => ({ ...current, earlyStartRequested: "" }));
                                        }}
                                        sx={{ pt: 0.25 }}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        The service begins on the contract start date, which is the first day of the{" "}
                                        {withdrawalPeriodDays}-day withdrawal period. I expressly request that it begin
                                        then, and I understand that I lose the right of withdrawal once the service has
                                        been fully performed, and that if I withdraw after it has begun I must pay for
                                        the part already provided.
                                    </Typography>
                                }
                            />
                            {errors.earlyStartRequested && (
                                <Typography variant="caption" color="error" display="block" sx={{ ml: 4 }}>
                                    {errors.earlyStartRequested}
                                </Typography>
                            )}
                        </Box>

                        <Stack spacing={1.5}>
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                A payment link for the next monthly fee is emailed to you automatically 10 days before each
                                monthly payment due date.
                            </Alert>

                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                {minimumValidityMonths === 0 ? (
                                    "You can request termination of this contract at any time."
                                ) : (
                                    <>
                                        This contract must stay active for at least{" "}
                                        <strong>
                                            {minimumValidityMonths} {minimumValidityMonths === 1 ? "month" : "months"}
                                        </strong>{" "}
                                        from the start date.
                                        {preview?.earliestTerminationDate && (
                                            <>
                                                {" "}With your chosen start date, the earliest possible end date is{" "}
                                                <strong>{formatEuropeanDate(preview.earliestTerminationDate)}</strong>.
                                            </>
                                        )}{" "}
                                        A termination request submitted for an earlier date will be rejected.
                                    </>
                                )}
                            </Alert>

                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                Joining here is paid by bank link, card or your account credit. If you want to pay with
                                Stebby funds, please contact the club
                                {clubEmail || clubPhone ? (
                                    <>
                                        {" "}at{" "}
                                        {clubEmail && <strong>{clubEmail}</strong>}
                                        {clubEmail && clubPhone && " / "}
                                        {clubPhone && <strong>{clubPhone}</strong>}
                                    </>
                                ) : (
                                    " directly"
                                )}
                                .
                            </Alert>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 2,
                        pb: isMobile ? "max(16px, env(safe-area-inset-bottom))" : 2,
                        gap: 1,
                        flexDirection: isMobile ? "column-reverse" : "row",
                        borderTop: 1,
                        borderColor: "divider",
                    }}
                >
                    <Button onClick={closeJoin} fullWidth={isMobile}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={continueToConfirmation}
                        disabled={previewLoading || !preview}
                        fullWidth={isMobile}
                        sx={{ py: isMobile ? 1.25 : undefined }}
                    >
                        Review and pay
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmOpen}
                onClose={submitting ? undefined : () => setConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        m: isMobile ? 0 : 2,
                    },
                }}
            >
                <DialogTitle sx={{ pr: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Typography variant="h6" component="span" fontWeight={700}>Confirm your contract</Typography>
                        <IconButton
                            onClick={() => setConfirmOpen(false)}
                            disabled={submitting}
                            edge="end"
                            aria-label="Close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <Typography>
                            You are concluding <strong>{selectedOffering?.name}</strong>, starting{" "}
                            {formatEuropeanDate(startDate)} and running until{" "}
                            {formatEuropeanDate(preview?.validUntil)}, with payments due on day {paymentDay} of each month.
                        </Typography>
                        <Box
                            sx={{
                                p: 2.5,
                                borderRadius: 2,
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                border: 1,
                                borderColor: "primary.light",
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                                Amount due now
                            </Typography>
                            <Typography variant="h4" fontWeight="bold" color="primary">
                                {formatCurrency(preview?.firstPaymentAmount, paymentCurrency)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {preview?.explanation}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Then {formatCurrency(preview?.monthlyPrice, paymentCurrency)} per month.
                            </Typography>
                        </Box>
                        {preview?.withdrawalDeadline && (
                            <Typography variant="body2" color="text.secondary">
                                Your {withdrawalPeriodDays}-day right of withdrawal runs from the start date, so you can
                                withdraw until <strong>{formatEuropeanDate(preview.withdrawalDeadline)}</strong>.
                            </Typography>
                        )}
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            Pressing the button below places an order with an obligation to pay. You will be taken to
                            the payment page, and the contract becomes active once the payment succeeds.
                        </Alert>
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 2,
                        pb: isMobile ? "max(16px, env(safe-area-inset-bottom))" : 2,
                        gap: 1,
                        flexDirection: isMobile ? "column-reverse" : "row",
                        borderTop: 1,
                        borderColor: "divider",
                    }}
                >
                    <Button onClick={() => setConfirmOpen(false)} disabled={submitting} fullWidth={isMobile}>
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        fullWidth={isMobile}
                        sx={{ py: isMobile ? 1.25 : undefined }}
                    >
                        {submitting
                            ? "Processing..."
                            : `Confirm order and pay ${formatCurrency(preview?.firstPaymentAmount, paymentCurrency)}`}
                    </Button>
                </DialogActions>
            </Dialog>

            {termsModalOpen && (
                <ContractTermsModal
                    open={termsModalOpen}
                    onClose={() => setTermsModalOpen(false)}
                    termsId="contract"
                />
            )}

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification((current) => ({ ...current, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={notification.severity}
                    onClose={() => setNotification((current) => ({ ...current, open: false }))}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BaseContractPlans;
